import React, { FC, useCallback, useContext, useEffect, useRef } from 'react';
import { App } from 'antd';
import {
  UNSAFE_DataRouterContext,
  useBeforeUnload,
  useBlocker,
} from 'react-router-dom';
import { useTranslate } from '@/translations';

const useWarnOnUnload = (when: boolean) => {
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!when) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [when],
    ),
  );
};

const BeforeUnloadPrompt: FC<{ when: boolean }> = ({ when }) => {
  useWarnOnUnload(when);
  return null;
};

const RouterUnsavedChangesPrompt: FC<{ when: boolean }> = ({ when }) => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const blocker = useBlocker(when);
  const prompted = useRef(false);
  useWarnOnUnload(when);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      prompted.current = false;
      return;
    }
    if (prompted.current) return;
    prompted.current = true;
    modal.confirm({
      title: t('unsavedDamageChangesTitle'),
      content: t('unsavedDamageChangesBody'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
    });
  }, [blocker, modal, t]);

  return null;
};

export const UnsavedDamageChangesPrompt: FC<{ when: boolean }> = ({ when }) => {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return dataRouter ? (
    <RouterUnsavedChangesPrompt when={when} />
  ) : (
    <BeforeUnloadPrompt when={when} />
  );
};
